import { Component, OnInit, OnDestroy, Renderer2, Input, HostListener } from '@angular/core';
import { INCPCCPayment } from 'epay-library';
import { EpayExtendedServiceService } from 'src/app/helpers/services/epay-extended-service.service';
import { Router } from '@angular/router';

@Component({
  selector: 'iframe-fort-point',
  templateUrl: './iframe-fort-point.component.html',
  styleUrls: ['./iframe-fort-point.component.css'],
})
export class ExternalIframeComponent implements OnInit, OnDestroy {
  @Input() creditCardPayment: INCPCCPayment = {} as INCPCCPayment;

  private _transactionId = '';
  private scriptAlreadyLoaded = false;

  @Input()
  set transactionId(value: string) {
    this._transactionId = value;
  }

  get transactionId(): string {
    return this._transactionId;
  }

  constructor(
    private renderer: Renderer2,
    private paymentService: EpayExtendedServiceService,
    private router: Router
  ) {}

  paymentDetails: any = {};

  ngOnInit(): void {
    const state = history.state;
    if (state?.transactionId) {
      this.transactionId = state.transactionId;
    } else {
      console.warn('[iframe] No transactionId found in history state');
    }
  
    const storedPayment = sessionStorage.getItem('creditCardPayment');
    if (storedPayment) {
      this.creditCardPayment = JSON.parse(storedPayment);
    } else {
      console.warn('[iframe] No creditCardPayment found in sessionStorage');
    }
  
    if (
      this.transactionId &&
      this.creditCardPayment &&
      !isNaN(this.creditCardPayment.totalAmount)
    ) {
      this.loadPaymentGatewayScript();
    } else {
      console.warn('[iframe] Missing transactionId or totalAmount. Script not loaded.');
    }
  
    window.addEventListener("message", this.handlePaymentMessage);
    
    // Listen for browser back navigation specifically
    window.addEventListener('popstate', this.handleBackNavigation);
  }

  ngOnDestroy(): void {
    // Clean up message listener
    window.removeEventListener("message", this.handlePaymentMessage);
    window.removeEventListener('popstate', this.handleBackNavigation);
  }

  handlePaymentMessage = (event: MessageEvent) => {
    console.log('Received postMessage:', event);
    const data = event.data;
    console.log('Parsed message data:', data);
  };

  handleBackNavigation = (event: PopStateEvent) => {
    console.log('Back navigation detected in iframe');
    sessionStorage.setItem('backNavigationFromIframe', 'true');
  };

  loadPaymentGatewayScript(): void {
    if (this.scriptAlreadyLoaded) return;
    const totalAmount = this.creditCardPayment?.totalAmount;
    if (totalAmount === undefined || isNaN(totalAmount)) {
      console.warn('[iframe] Aborting script load: totalAmount is missing or invalid:', totalAmount);
      return;
    }

    this.scriptAlreadyLoaded = true;

    const paymentFormElement = document.getElementById('payment-form');
    if (!paymentFormElement) {
      console.error('Payment form element not found in the DOM.');
      return;
    }

    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.text = `
      (function() {
        const options = {
          data: "eyJkYmFJZCI6IjIyMTM0NyIsInRlcm1pbmFsSWQiOiI0ODA5NzMiLCJ0aHJlZWRzIjoiRGlzYWJsZWQiLCJyZXR1cm5VcmwiOiJodHRwczpcL1wvcGF5bWVudHMuc21hcnRjaGlsZHN1cHBvcnQuY29tXC9ORFwvY3JlZGl0Y2FyZDxzdGF0dXM%2BP2V4dGVybmFsSWQ9PGV4dGVybmFsSWQ%2BJmlkPTxpZD4iLCJyZXR1cm5VcmxOYXZpZ2F0aW9uIjoidG9wIiwibG9nbyI6bnVsbCwidmlzaWJsZU5vdGUiOm51bGwsInJlcXVlc3RDb250YWN0SW5mbyI6IlllcyIsInJlcXVlc3RCaWxsaW5nSW5mbyI6IlllcyIsInJlcXVlc3RTaGlwcGluZ0luZm8iOm51bGwsInNlbmRSZWNlaXB0IjoiWWVzIiwib3JpZ2luIjoiV0VCIiwiaGFzaCI6ImQzZDlkZjU1OWM3MGIxODU0NDUyNTc2NzQzZjgzZWUxIiwiY29udGFjdEluZm8iOnsiY29udGFjdE5hbWUiOiIiLCJjb250YWN0RW1haWwiOiIiLCJjb250YWN0UGhvbmUiOiIifSwiYmlsbGluZ0luZm8iOnsiYmlsbGluZ0NvdW50cnkiOm51bGwsImJpbGxpbmdTdHJlZXQiOiIiLCJiaWxsaW5nU3RyZWV0MiI6IiIsImJpbGxpbmdDaXR5IjoiIiwiYmlsbGluZ1N0YXRlIjpudWxsLCJiaWxsaW5nWmlwIjoiIn19",
          feeType: "percent",
          contactInfo: { name: "", email: "", phone: "" },
          billingInfo: {
            country: "United States",
            street: "",
            street2: "",
            city: "",
            state: "",
            zip: ""
          },
          dbaId: 221347,
          terminalId: 480973,
          threeds: 'Disabled',
          hash: '6f2331ecda8e7511097103fd232569f0',
          externalId: '${this.transactionId}',
          amount: '${this.creditCardPayment.amount}',
          fee: '2.75',
          returnURL: '',
          returnUrlNavigation: 'top',
          visibleNote: 'No',
          requestContactInfo: 'Yes',
          requestBillingInfo: 'Yes',
          sendReceipt: 'Yes',
          origin: 'WEB'
        };

        const initializePaymentGateway = function() {
          if (typeof PaymentGateway === 'undefined') {
            console.error('PaymentGateway is undefined.');
            return;
          }

          new PaymentGateway({
            target: "payment-form",
            options: options
          });

          // Add accessibility attributes to the iframe after it's created
          setTimeout(() => {
            const iframe = document.querySelector('#payment-form iframe');
            if (iframe) {
              iframe.setAttribute('title', 'Credit Card Payment Form');
              iframe.setAttribute('aria-label', 'Credit Card Payment Form - Enter your payment information');
            }
          }, 1000);

          window.paymentGatewayInitialized = true;
        };

        if (typeof window.PaymentGateway === "undefined") {
          window.paymentGatewayInitialized = false;
          window.webroot = "https://fppdashboard.com";
          const e = document.createElement("script");
          e.async = true;
          e.src = window.webroot + "/js/gateway/payment.js?v=" + Date.now();
          document.head.appendChild(e);
          e.onload = initializePaymentGateway;
        } else {
          window.paymentGatewayInitialized = false;
          initializePaymentGateway();
        }
      })();
    `;

    this.renderer.appendChild(document.body, script);
  }
}
